import { ice } from '@halvard/rules';
import type { Square } from '../types.js';
import { slideFate } from './timeline.js';

/**
 * What the best shove on an enemy would do: `works`, sinks an enemy, sinks one of the shover's
 * own warriors (a warning, even if it also sinks enemies), or `fails` (the shield wall holds).
 */
export type ShoveTone = 'works' | 'sinks-enemy' | 'sinks-ally' | 'fails';

/** The numbers over an enemy the selected warrior can reach. */
export interface ShoveBadge {
  target: Square;
  strength: number;
  wall: number;
  tone: ShoveTone;
}

/** One way to shove an enemy: the walk, the step into it, and the direction it pushes. */
export interface ShoveRoute {
  option: ice.ShoveOption;
  tone: ShoveTone;
  /** `strong`: the shove a click would play. `faint`: another approach to the same enemy. */
  emphasis: 'strong' | 'faint';
}

/** Why a sliding warrior is lost: off the lake, into a hole, a 2-level fall, or a crush. */
export type SinkReason = 'off' | 'hole' | 'fall' | 'crush';

/** Where a warrior in the shoved line would end up. */
export interface ShoveGhost {
  warrior: ice.Warrior;
  to: Square;
  sunk: SinkReason | null;
}

export interface IceHints {
  /** Squares to place on (placement) or walk to (selected warrior). */
  moves: Square[];
  badges: ShoveBadge[];
  routes: ShoveRoute[];
  ghosts: ShoveGhost[];
  /** The shove a click on the focused square would play, if any. */
  choice: ice.ShoveOption | null;
}

/** Nothing to show: not this screen's turn. */
export const NO_HINTS: IceHints = { moves: [], badges: [], routes: [], ghosts: [], choice: null };

function same(a: Square, b: Square): boolean {
  return a.r === b.r && a.c === b.c;
}

/** Why a slide loses its warrior, from the state before the shove; `null` if it lands. */
export function sinkReason(state: ice.IceState, slide: ice.Slide): SinkReason | null {
  const fate = slideFate(state.heights, slide);
  return fate === 'lands' ? null : fate;
}

export function shoveTone(state: ice.IceState, option: ice.ShoveOption): ShoveTone {
  if (!option.legal) return 'fails';
  const shover = state.warriors.find((w) => w.id === option.action.id);
  const sides = option.slides
    .filter((s) => s.sunk)
    .map((s) => state.warriors.find((w) => w.id === s.id)?.side);
  if (sides.includes(shover?.side)) return 'sinks-ally';
  return sides.length > 0 ? 'sinks-enemy' : 'works';
}

/** Legal shoves first, then the ones that sink more enemies and fewer allies, then fewer steps. */
function score(state: ice.IceState, option: ice.ShoveOption): number {
  const shover = state.warriors.find((w) => w.id === option.action.id);
  let value = option.legal ? 1000 : 0;
  for (const slide of option.slides) {
    if (!slide.sunk) continue;
    const side = state.warriors.find((w) => w.id === slide.id)?.side;
    value += side === shover?.side ? -50 : 100;
  }
  return value - option.path.length;
}

function best(state: ice.IceState, options: ice.ShoveOption[]): ice.ShoveOption | null {
  let top: ice.ShoveOption | null = null;
  for (const option of options) {
    if (!top || score(state, option) > score(state, top)) top = option;
  }
  return top;
}

function ghostsOf(state: ice.IceState, option: ice.ShoveOption): ShoveGhost[] {
  return option.slides.flatMap((slide) => {
    const warrior = state.warriors.find((w) => w.id === slide.id);
    return warrior ? [{ warrior, to: slide.to, sunk: sinkReason(state, slide) }] : [];
  });
}

/**
 * Hints for the side to move.
 *
 * - While placing: the free squares of its territory.
 * - With a warrior selected: the squares it can walk to and a badge on every enemy it can shove.
 * - `target` is an enemy the player has picked. Its routes are drawn, one per approach square,
 *   and `focus` (the hovered or keyboard square) picks one: only that route stays.
 * - Without a target, focusing an enemy previews its routes the same way.
 */
export function iceHints(
  state: ice.IceState,
  selectedId: string | null,
  target: Square | null,
  focus: Square | null,
): IceHints {
  if (state.phase === 'placing') {
    const squares = ice
      .legalIceActions(state)
      .flatMap((a) => (a.kind === 'place' ? [a.at] : []))
      .filter((sq, i, all) => all.findIndex((other) => same(other, sq)) === i);
    return { ...NO_HINTS, moves: squares };
  }
  const warrior = state.warriors.find((w) => w.id === selectedId);
  if (state.phase !== 'playing' || !warrior || warrior.side !== state.turn) return NO_HINTS;

  const found = ice.reach(state, warrior);
  const moves: Square[] = [];
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      const cost = ice.costTo(found, r, c);
      if (cost > 0 && cost !== Infinity) moves.push({ r, c });
    }
  }

  const options = ice.shoveOptions(state, warrior);
  const byTarget = new Map<string, ice.ShoveOption[]>();
  for (const option of options) {
    const key = `${option.action.target.r}:${option.action.target.c}`;
    byTarget.set(key, [...(byTarget.get(key) ?? []), option]);
  }
  const badges: ShoveBadge[] = [...byTarget.values()].flatMap((group) => {
    const top = best(state, group);
    return top
      ? [
          {
            target: top.action.target,
            strength: top.strength,
            wall: top.wall,
            tone: shoveTone(state, top),
          },
        ]
      : [];
  });

  const aimed = target ?? focus;
  const group = aimed ? (byTarget.get(`${aimed.r}:${aimed.c}`) ?? []) : [];
  if (group.length === 0) return { moves, badges, routes: [], ghosts: [], choice: null };

  const picked = target && focus ? group.find((o) => same(o.action.from, focus)) : undefined;
  const strong = picked ?? best(state, group);
  const routes: ShoveRoute[] = (picked ? [picked] : group).map((option) => ({
    option,
    tone: shoveTone(state, option),
    emphasis: option === strong ? 'strong' : 'faint',
  }));
  return {
    moves,
    badges,
    routes,
    ghosts: strong ? ghostsOf(state, strong) : [],
    choice: strong?.legal ? strong : null,
  };
}
